/**
 * `<Term k>`: a word on the combat surface that opens its plain-language rule
 * (`projects/combat-glossary.md` 2.1, `decisions/0186` item 5). The rule comes from
 * {@link glossaryEntry}; this component only shows it.
 *
 * The term renders its children with a dotted underline. Its popover opens on mouse hover, on
 * keyboard focus, and on a tap or click (which toggles its pin, so touch has a way in; a click on a
 * term already showing from hover or focus pins it rather than closing it). Esc and a press
 * anywhere outside close it, and only one popover is open at a time: opening one closes the last.
 * Inside COMBAT.app an open popover is a layer-1 Esc entry (`decisions/0189`), so the press it
 * takes goes no further and the next Esc reaches the layer under it (a command-menu screen goes
 * back). Elsewhere it listens for Esc itself.
 * The popover is portalled to `document.body` with `position: fixed`, so no scroll host clips it,
 * and {@link termPlacement} flips it above the term or back inside the viewport at an edge.
 *
 * A key nothing answers renders the children plain, with no underline, so a surface can wrap every
 * label without checking first.
 *
 * `inline` is for a term inside another control (a command-menu row is a `<button>`): the term is
 * not focusable itself, a click on it toggles the popover without reaching the control, and the
 * host passes `open` while the control has keyboard focus.
 *
 * `hoverOnly` is for a term inside an ACTIONABLE row (a command-menu mount row's weapon tags): a
 * player clicks anywhere on that row to arm it, so the term must never take the click. It opens on
 * mouse hover (and on the host's `open`) only; a click neither pins it nor stops it reaching the
 * row. Touch reads those rules through the row's own (i) instead. Everywhere else (the mech sheet,
 * headings) keeps click-to-open.
 *
 * Styling is `term.css` (`term-*`), and deliberately carries nothing of any one surface, because
 * `unit-inspect-card` will host it too.
 */
import { useContext, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { CompendiumContext } from "../CompendiumProvider";
import { EscLayersContext, useEscEntry } from "../encounter/escLayers";
import { RulesText } from "../RulesText";
import { glossaryEntry, type GlossaryCtx, type GlossaryEntry, type GlossaryKey } from "./combatGlossary";
import "./term.css";

/** Gap between the term and its popover, and the popover's minimum margin from the viewport edge. */
const GAP_PX = 6;
const EDGE_PX = 8;

/**
 * Place a `w`×`h` popover under the term's rect, flipping above it at the bottom edge and clamping it
 * inside the viewport. When it fits on neither side it slides up over the term until its bottom clears
 * the viewport edge: the popover cannot scroll, so a cut-off tail is unreadable.
 */
export function termPlacement(args: { rect: { left: number; top: number; bottom: number }; w: number; h: number; vw: number; vh: number }): { left: number; top: number; flipped: boolean } {
  const { rect, w, h, vw, vh } = args;
  const left = Math.max(EDGE_PX, Math.min(rect.left, vw - w - EDGE_PX));
  const below = rect.bottom + GAP_PX;
  const flipped = below + h > vh - EDGE_PX && rect.top - GAP_PX - h >= EDGE_PX;
  if (flipped) return { left, top: rect.top - GAP_PX - h, flipped };
  return { left, top: Math.max(EDGE_PX, Math.min(below, vh - EDGE_PX - h)), flipped };
}

/** The one open popover's closer: opening another term calls it first. */
let closeOpen: (() => void) | null = null;

export function Term({
  k,
  ctx,
  inline = false,
  hoverOnly = false,
  open,
  className,
  badges = [],
  note,
  fallback,
  entry: given,
  label,
  children,
}: {
  k: GlossaryKey;
  /** Overrides the app's compendium (for `tag:` keys) and carries a tag's `{VAL}`. */
  ctx?: GlossaryCtx;
  /** A term inside another control: not focusable, and a click toggles it without reaching the control. */
  inline?: boolean;
  /** A term inside an actionable row: hover only, never focusable, and a click passes straight through to the row. */
  hoverOnly?: boolean;
  /** An `inline` or `hoverOnly` term's host has keyboard focus: show the popover while true. */
  open?: boolean;
  className?: string;
  /** Short chips beside the popover's title (a status's duration, "GM ruling"). */
  badges?: readonly string[];
  /** Lines about this instance, above the rule (a GM ruling's reason). */
  note?: ReactNode;
  /** What the popover shows when the registry has no entry (a homebrew status); without it the term renders plain. */
  fallback?: GlossaryEntry;
  /**
   * The entry itself, when the host holds text more specific than the registry's (a command-menu
   * row's own blurb, which a talent grant can carry). `k` then only labels the term.
   */
  entry?: GlossaryEntry;
  /** The term's accessible name, when its children are not text (the command menu's diamonds). */
  label?: string;
  children: ReactNode;
}) {
  const app = useContext(CompendiumContext);
  const entry = given ?? glossaryEntry(k, { ...(app ? { compendium: app.compendium } : {}), ...ctx }) ?? fallback;
  const ref = useRef<HTMLSpanElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const id = useId();
  const [hover, setHover] = useState(false);
  const [focused, setFocused] = useState(false);
  const [pinned, setPinned] = useState(false);
  // Esc or an outside press while the host holds `open`: stay closed until `open` next turns on.
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    if (!open) setDismissed(false);
  }, [open]);

  const shown = entry !== undefined && (hover || focused || pinned || (open === true && !dismissed));

  const closeRef = useRef<() => void>(() => {});
  closeRef.current = () => {
    setHover(false);
    setFocused(false);
    setPinned(false);
    if (open) setDismissed(true);
  };

  const esc = useContext(EscLayersContext);
  useEscEntry(esc, { layer: "popover", id: `term:${id}`, active: shown, onDismiss: () => closeRef.current() });

  useEffect(() => {
    if (!shown) return;
    const close = () => closeRef.current();
    if (closeOpen && closeOpen !== close) closeOpen();
    closeOpen = close;
    // Outside COMBAT.app's registry the popover takes its own Esc, and the surface under it never sees that press.
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      close();
    };
    const onPress = (e: PointerEvent) => {
      if (e.target instanceof Node && ref.current?.contains(e.target)) return;
      close();
    };
    if (!esc) document.addEventListener("keydown", onKey, true);
    document.addEventListener("pointerdown", onPress, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      document.removeEventListener("pointerdown", onPress, true);
      if (closeOpen === close) closeOpen = null;
    };
  }, [shown, esc]);

  useLayoutEffect(() => {
    const el = ref.current;
    const pop = popRef.current;
    if (!shown || !el || !pop) return;
    const p = termPlacement({ rect: el.getBoundingClientRect(), w: pop.offsetWidth, h: pop.offsetHeight, vw: window.innerWidth, vh: window.innerHeight });
    pop.style.left = `${p.left}px`;
    pop.style.top = `${p.top}px`;
    pop.dataset.flipped = String(p.flipped);
    pop.style.visibility = "visible";
  }, [shown]);

  if (entry === undefined) return <span className={className}>{children}</span>;

  const popId = `term-${id}`;
  // Neither variant is focusable on its own: its host control is.
  const hosted = inline || hoverOnly;
  return (
    <span
      ref={ref}
      className={`term${shown ? " is-open" : ""}${className ? ` ${className}` : ""}`}
      data-term={k}
      tabIndex={hosted ? undefined : 0}
      role={hosted ? undefined : "button"}
      aria-expanded={hosted ? undefined : shown}
      aria-label={label}
      aria-describedby={shown ? popId : undefined}
      onPointerEnter={(e) => {
        if (e.pointerType === "mouse") setHover(true);
      }}
      onPointerLeave={(e) => {
        if (e.pointerType === "mouse") setHover(false);
      }}
      onFocus={hosted ? undefined : () => setFocused(true)}
      onBlur={hosted ? undefined : () => setFocused(false)}
      onClick={(e) => {
        // The row under a hover-only term takes the click, untouched.
        if (hoverOnly) return;
        if (inline) {
          e.stopPropagation();
          e.preventDefault();
        }
        // Toggle on the pin, not on `shown`: a tap focuses this span and a mouse hovers it before the
        // click lands, so the popover is already showing, and the click must pin it, not close it.
        if (pinned || (open === true && !dismissed)) closeRef.current();
        else setPinned(true);
      }}
    >
      {children}
      {shown &&
        createPortal(
          <div ref={popRef} id={popId} className="term-pop" role="tooltip" data-term-pop={k} style={{ visibility: "hidden" }}>
            <div className="term-pop-kind">{entry.kind}</div>
            <div className="term-pop-head">
              <span className="term-pop-title">{entry.title}</span>
              {badges.map((b) => (
                <span key={b} className="term-pop-badge">
                  {b}
                </span>
              ))}
            </div>
            {note}
            {entry.text && <RulesText className="term-pop-text" html={entry.text} />}
          </div>,
          document.body,
        )}
    </span>
  );
}

/**
 * A visible (i) that opens `k`'s entry through {@link Term}: for a label whose explainer is a
 * `card:` key (the heat track's), so the rule has a way in that doesn't depend on finding the
 * dotted underline. Not `inline`: it sits beside a label, not inside a control, so it takes focus
 * and a click pins it.
 */
export function InfoTerm({ k, label }: { k: GlossaryKey; label: string }) {
  return (
    <Term k={k} className="term-info" label={label}>
      <span className="term-info-glyph" aria-hidden="true">
        i
      </span>
    </Term>
  );
}
